import React, { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { manifesto } from '../data/manifesto';
import { focusInView } from '../utils/reveal';
interface ManifestoSectionProps {
  /** The serif line — once it's scrolled well up, Pixel sneaks into the spot below it */
  lineRef: RefObject<HTMLParagraphElement>;
  /** Pixel's spot between the line and the headline (his chat renders inside it) */
  slotRef: RefObject<HTMLDivElement>;
  /** The big headline — the black dark-mode sweep starts once it reaches 70% of the screen height */
  headingRef: RefObject<HTMLHeadingElement>;
}

/**
 * After the showreel, centered like the reference at 1366px:
 * a quiet ~24px serif line, Pixel's spot, then a huge two-line headline (~7.3vw).
 * Both come into focus on scroll, like the hero text.
 */
export function ManifestoSection({
  lineRef,
  slotRef,
  headingRef
}: ManifestoSectionProps) {
  const reduce = useReducedMotion();
  return <section className="relative flex w-full flex-col items-center px-3 pb-[clamp(15rem,30vw,33rem)] pt-[clamp(3rem,5vw,6rem)] text-center" data-id="element-129" data-mp="2zpiklc19rsum">
      <motion.p ref={lineRef} className="font-serif font-medium md:font-normal text-[1.375rem] md:text-[clamp(1.125rem,1.75vw,1.75rem)] leading-snug tracking-[-0.01em] text-white mix-blend-difference relative z-[42]" {...focusInView(0, reduce, {
      amount: 0.8
    })} data-id="element-130" data-mp="2ifd2avcls9fk">
        {/* Mobile: breaks into two balanced lines (second slightly longer); desktop: one line */}
        {manifesto.lineMobile[0]} <br className="md:hidden" data-id="element-131" data-mp="317fmuhsd89v3" />
        {manifesto.lineMobile[1]}
      </motion.p>

      {/* Pixel's spot (same height as the hero so he keeps his size): his pinned position hands over
          to this space as it scrolls under him; he chats here and docks from here */}
      <div ref={slotRef} data-drone-dock="true" data-drone-keep-scale="true" data-drone-mobile-left="true"
    // Phones: Pixel sits smaller on the left, so his chat starts left of centre and has the right side
    className="chat-spot-mobile-left relative h-[46vh] min-h-[400px] w-full" data-id="element-132" data-mp="1ks4o7lexm2nl" />

      <h2 ref={headingRef}
    // Inverts against whatever is behind it: black on the white page, white exactly where the rising black covers it
    className="text-[clamp(2.75rem,7.3vw,8.75rem)] font-bold leading-[0.95] tracking-[-0.055em] text-white mix-blend-difference" data-id="element-133" data-mp="1n5rqg6rmf67b">
        {manifesto.heading.map((line, i) => <motion.span key={line} className="block pb-[0.05em]" {...focusInView(i * 0.2, reduce, {
        y: 14,
        blur: 14,
        amount: 0.5
      })} data-id="element-134" data-mp="14870ewd4e1l4" data-mpi={i}>
            {line}
          </motion.span>)}
      </h2>
    </section>;
}